// The status chips every payment-method list shows, in one place (web):
//   • "Default for salary" — THE destination payroll pays (one per employee);
//   • "Preferred bank/wallet" — the per-type preference, kept for compatibility
//     and only shown on a method that is NOT the default (so the two never
//     compete for attention);
//   • the verification status.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/features/shared/components/ui/badge';
import type { PaymentMethod } from '@/lib/hrApi';

const STATUS: Record<string, { key: string; fallback: string; variant: 'success' | 'warning' | 'danger' | 'secondary' }> = {
  APPROVED: { key: 'destinations.status.approved', fallback: 'Approved', variant: 'success' },
  PENDING_VERIFICATION: { key: 'destinations.status.pending', fallback: 'Pending verification', variant: 'warning' },
  REJECTED: { key: 'destinations.status.rejected', fallback: 'Rejected', variant: 'danger' },
  INACTIVE: { key: 'destinations.status.inactive', fallback: 'Deactivated', variant: 'secondary' },
};

export const PaymentMethodChips: React.FC<{ method: PaymentMethod; className?: string }> = ({ method, className }) => {
  const { t } = useTranslation('payroll');
  const status = STATUS[method.status] ?? STATUS.INACTIVE;
  return (
    <span className={`inline-flex flex-wrap items-center justify-end gap-1.5 ${className ?? ''}`}>
      {method.is_default && (
        <Badge variant="default">
          <CheckCircle2 className="h-3 w-3" aria-hidden />
          {t('destinations.default', { defaultValue: 'Default for salary' })}
        </Badge>
      )}
      {!method.is_default && method.is_primary && (
        <Badge variant="outline">
          {method.type === 'BANK'
            ? t('destinations.preferredBank', { defaultValue: 'Preferred bank' })
            : t('destinations.preferredWallet', { defaultValue: 'Preferred wallet' })}
        </Badge>
      )}
      <Badge variant={status.variant}>{t(status.key, { defaultValue: status.fallback })}</Badge>
    </span>
  );
};

/** Default first, then approved, then the rest (stable within each group). */
export function sortForDisplay(methods: PaymentMethod[]): PaymentMethod[] {
  const rank = (m: PaymentMethod) => (m.is_default ? 0 : m.status === 'APPROVED' ? 1 : 2);
  return [...methods].sort((a, b) => rank(a) - rank(b));
}

/** May this method be made the payroll default? (server re-checks) */
export const canBeDefault = (m: PaymentMethod) => m.status === 'APPROVED' && !m.is_default;

/** May this method be deactivated? (kept as history; server re-checks who may) */
export const canDeactivate = (m: PaymentMethod) => m.status !== 'INACTIVE';

/**
 * PM11: the method whose deactivation (or edit / rejection) put the employee's
 * salary ON HOLD — it was the destination payroll paid, and no new default has
 * been chosen since — or null. Mirrors employee-service's hold rule; payroll
 * errors a held employee instead of paying any other method.
 */
export function payoutHold(methods: PaymentMethod[]): PaymentMethod | null {
  if (methods.some((m) => m.is_default && m.status === 'APPROVED')) return null;
  const released = methods.filter((m) => !!m.default_released_at);
  if (released.length === 0) return null;
  return released.reduce((a, b) => (String(b.default_released_at) > String(a.default_released_at) ? b : a));
}

const maskedLabel = (m: PaymentMethod) =>
  (m.type === 'BANK' ? [m.bank_name, m.account_masked] : [m.mobile_money_provider, m.phone_masked]).filter(Boolean).join(' ');

/** The salary-on-hold notice, for the employee's own tab and for HR. */
export const PayoutHoldBanner: React.FC<{ methods: PaymentMethod[]; self?: boolean }> = ({ methods, self }) => {
  const { t } = useTranslation('payroll');
  const held = payoutHold(methods);
  if (!held) return null;
  return (
    <div role="alert" data-testid="payout-hold-banner"
      className="flex items-start gap-3 rounded-xl border border-warning bg-warning-soft p-4 text-sm text-warning-strong">
      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden />
      <span>
        {self
          ? t('destinations.holdSelf', {
              defaultValue:
                'Your salary is on hold: {{label}} was your salary destination and is no longer active. Choose a new default payment method — payroll never sends your salary to another account automatically.',
              label: maskedLabel(held),
            })
          : t('destinations.holdHr', {
              defaultValue:
                'Salary on hold: {{label}} was the salary destination and is no longer active. Payroll will not pay this employee until a new default payment method is set — it is never redirected automatically.',
              label: maskedLabel(held),
            })}
      </span>
    </div>
  );
};
